<template>
  <v-autocomplete
    v-model="selected"
    v-model:search-input="query"
    variant="solo"
    density="compact"
    no-filter
    hide-no-data
    auto-select-first
    clearable
    class="ml-4"
    data-testid="map-search-bar"
    :menu-icon="null"
    :error="placeNotFound"
    :error-messages="placeNotFound ? t('map.location_not_found') : ''"
    :placeholder="t('search')"
    :loading="loading"
    :items="items"
    item-title="text"
    item-value="value"
    :allow-overflow="false"
    :menu-props="{
      maxWidth: '300px',
      offsetY: true,
      rounded: true,
    }"
    @click:clear="handleClearInput"
    @update:search="handleSearchInput"
  >
    <template #prepend-inner>
      <v-icon>mdi-magnify</v-icon>
    </template>
    <template #item="{ props, item }">
      <v-list-item v-bind="props">
        <div class="item-container mt-1 mb-1">
          <div>
            <img
              v-if="item.value.icon"
              :src="item.value.icon"
              alt="location icon"
            >
          </div>
          <span>{{ item.value.postcode }}</span>
          <span>{{ item.value.name }}</span>
          <span>{{ item.value.state }}</span>
        </div>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue';
import type { LatLngBoundsExpression, LatLngTuple } from 'leaflet';
import { useI18n } from 'vue-i18n';

// TODO: this should probably be globally defined somewhere
type Bundesland =
  | 'BGL'
  | 'KNT'
  | 'NOE'
  | 'OOE'
  | 'SAL'
  | 'STMK'
  | 'TIR'
  | 'VBG'
  | 'WIE';

type BBox = [number, number, number, number];

export interface GeoJsonGeom {
  type: 'Point' | 'Polygon' | 'MultiPolygon';
  coordinates: Array<any>;
}

interface AddressDetails {
  id: number;
  name: string;
  postcode: string | null | undefined;
  state: Bundesland;
  boundingbox: BBox;
  geojson: GeoJsonGeom;
  icon: string | undefined;
}

interface LocationItem {
  text: string;
  value: AddressDetails;
}

// TODO: put typedefs somewhere else
export interface ZoomProps {
  latlng: LatLngBoundsExpression;
  geojson: GeoJsonGeom;
  id: number;
}

const stateMap: { [unit: string]: Bundesland } = {
  'AT-1': 'BGL',
  'AT-2': 'KNT',
  'AT-3': 'NOE',
  'AT-4': 'OOE',
  'AT-5': 'SAL',
  'AT-6': 'STMK',
  'AT-7': 'TIR',
  'AT-8': 'VBG',
  'AT-9': 'WIE',
  Burgenland: 'BGL',
  Kärnten: 'KNT',
  Niederösterreich: 'NOE',
  Ostösterreich: 'OOE',
  Salzburg: 'SAL',
  Steiermark: 'STMK',
  Tirol: 'TIR',
  Voralberg: 'VBG',
  Wien: 'WIE',
};

export default defineComponent({
  name: 'MapSearchBar',
  emits: ['zoomToLocation', 'resetSearch'],
  setup(_, { emit }) {
    const { t } = useI18n();

    let timeout: number | undefined;
    const loading = ref(false);
    const placeNotFound = ref(false);
    const items = ref<LocationItem[]>([]);
    const query = ref<string>('');
    const selected = ref<AddressDetails | null>(null);

    const emitZoomToLocation = (
      latlng: LatLngBoundsExpression,
      geojson: GeoJsonGeom,
      id: number
    ): ZoomProps => {
      const payload: ZoomProps = { latlng, geojson, id };
      emit('zoomToLocation', payload);
      return payload;
    };

    const emitResetSearch = (): void => {
      emit('resetSearch');
    };

    watch(query, (newQuery, oldQuery) => {
      if (newQuery === oldQuery) return;
      handleLocationQuery(newQuery);
    });

    watch(selected, (item) => {
      handleItemSelection(item);
    });

    // handle user selection from the dropdown
    const handleItemSelection = (item: AddressDetails | null): void => {
      if (!item) return;
      if (!item.boundingbox || item.boundingbox.length < 4) return;
      const bbox: BBox = item.boundingbox;
      const bounds: Array<LatLngTuple> = [
        [bbox[0], bbox[2]],
        [bbox[1], bbox[3]],
      ];
      emitZoomToLocation(bounds, item.geojson, item.id);
    };

    // handle user search input
    const handleSearchInput = (query: string | null): void => {
      if (!query) return;

      // debounce search
      if (timeout) clearTimeout(timeout);

      timeout = window.setTimeout(async () => {
        const fetched = await fetchItems(query);
        items.value = parseItems(query, fetched);
        placeNotFound.value = items.value.length === 0;
      }, 1000);
    };

    const handleClearInput = (): void => {
      resetSearch();
    };

    const resetSearch = (): void => {
      loading.value = false;
      items.value = [];
      selected.value = null;
      // It happens, that the query is set to null, when the
      // search input does not match any item and the focus is removed
      // from the inputfield. This should not emit a reset, because there simply
      // was no valid search result. Instead, when deleting a valid search result
      // we would like to emit a reset and inform, that we purposely removed a valid
      // state.
      if (query.value === '') emitResetSearch();
    };

    const handleLocationQuery = (queryStr: string): void => {
      if (timeout) clearTimeout(timeout);

      placeNotFound.value = false;
      loading.value = true;

      if (!queryStr) {
        resetSearch();
        return;
      }

      timeout = window.setTimeout(async () => {
        const fetched = await fetchItems(queryStr);
        items.value = parseItems(queryStr, fetched);

        if (items.value.length === 0) placeNotFound.value = true;

        loading.value = false;
      }, 1000);
    };

    const fetchItems = async (queryStr: string): Promise<Array<any>> => {
      const result = await fetch(
        'https://nominatim.openstreetmap.org/search?' +
          new URLSearchParams({
            q: queryStr,
            addressdetails: '1',
            limit: '10',
            format: 'json',
            /*
            TODO: Instead of limiting to countrycodes, it might be better to limit
            the search on the bbox of the dataset. For this set 'viewbox=<bbox>'
            and 'bounded=1'. For more information see: https://nominatim.org/release-docs/develop/api/Search/#result-limitation

            related ticket: https://vcgitlab.zamg.ac.at/dataportal/api-management/api-frontend/-/issues/127
            */
            countrycodes: 'AT',
            polygon_geojson: '1',
          }),
        {
          headers: { 'Accept-Language': 'de' },
        }
      );
      return await result.json();
    };

    const parseItems = (queryStr: string, itemsArr: Array<Record<string, any>>): Array<LocationItem> => {
      const allowedLocationClasses = ['place', 'boundary', 'natural'];
      return itemsArr
        .filter((item) => allowedLocationClasses.includes(item.class))
        .map((item) => {
          // The lat and lon of the coords are served in reverse
          let geojson: GeoJsonGeom;
          switch (item.geojson?.type) {
            case 'Point':
              geojson = {
                ...item.geojson,
                coordinates: item.geojson.coordinates.reverse(),
              };
              break;
            case 'Polygon':
              geojson = {
                ...item.geojson,
                coordinates: item.geojson.coordinates[0].map((coord: any[]) =>
                  coord.reverse()
                ),
              };
              break;
            case 'MultiPolygon':
              geojson = {
                ...item.geojson,
                coordinates: item.geojson.coordinates.map((polygon: any[]) =>
                  polygon[0].map((coord: any[]) => coord.reverse())
                ),
              };
              break;
            default:
              geojson = { type: 'Point', coordinates: [item.lat, item.lon] };
          }

          const postcode = parseInt(item.address.postcode) // only show numerical PLZs
            ? String(item.address.postcode)
            : null;

          // Here is some hacky logic to get rid of the unnecessary mentioning of
          // Oesterreich in the name as well as get rid of postcode. Getting rid of
          // state seems too much of a hassle tbh
          let displayNameArr: Array<string> = item.display_name
            .split(',')
            .map((part: string) => part.trim());
          if (displayNameArr.length >= 2) {
            if (displayNameArr.slice(-1)[0].toLowerCase() === 'österreich') {
              displayNameArr.splice(-1);
            }
          }
          if (postcode) {
            displayNameArr = displayNameArr.filter((part) => part !== postcode);
          }

          const locationItem: LocationItem = {
            text: queryStr,
            value: {
              id: item.place_id,
              name: displayNameArr.join(', '),
              postcode,
              state:
                stateMap[item.address['ISO3166-2-lvl4'] || item.address.state],
              boundingbox: item.boundingbox,
              geojson,
              icon: item.icon,
            },
          };
          return locationItem;
        })
        .filter((item) => item.text); // filter out all items which end up not having a text/name
    };

    return {
      t,
      loading,
      placeNotFound,
      items,
      query,
      selected,
      handleSearchInput,
      handleClearInput,
    };
  },
});
</script>

<style scoped>
.item-container {
  display: grid;
  grid-template-columns: 16px 38px auto auto;
  gap: 12px;
  width: 100%;
}

.item-container > * {
  display: flex;
  align-items: center;
}

.item-container > :nth-child(2) {
  font-weight: 600;
}

.item-container > :nth-child(4) {
  margin-left: auto;
  color: grey;
}

/* Long descriptions should be readable. We achieve this with line breaks. */
:deep(.v-list-item__title) {
  white-space: normal;
}

/*
  Below we fix the colors for the dropdown items.
  Unfortunately vuetify/MUI is very opinionated, so we have to override the css
  in this manner.
*/

/* text color of active item should be the primary color */
:deep(.v-theme--light.v-list-item--active) span {
  color: rgb(var(--v-theme-primary)) !important;
}

/*
  the item's background color depends on the parent's color.
  We would like to have the active item have the darkest gradient.
*/
:deep(.v-theme--light.v-list-item--active)::before {
  opacity: 0.18;
}

/*
  If the active item is active AND highlighted we want to have the
  background even darker.
*/
:deep(.v-theme--light.v-list-item--highlighted.v-list-item--active)::before {
  opacity: 0.22;
}

/*
  Override the color of highlighted items to have primary color
*/
:deep(.v-theme--light.v-list-item--highlighted)::before {
  color: rgb(var(--v-theme-primary)) !important;
  opacity: 0.08;
}

/*
  Override the color of hovered items to have primary color and very
  light gradient. This only holds for non-active, non-highlighted
*/
:deep(.v-theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--highlighted):hover)::before {
  background-color: rgb(var(--v-theme-primary)) !important;
  opacity: 0.03;
}

/*
  Override the color of the on-click ripple effect to have primary color
*/
:deep(.v-theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--highlighted):hover::before .v-ripple__container) {
  color: rgb(var(--v-theme-primary)) !important;
}

:deep(.v-theme--light .v-ripple__container) {
  color: rgb(var(--v-theme-primary)) !important;
}

:deep(.v-field__input) {
  height: 32px !important;
  min-height: 32px !important;
}
</style>
